import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { Avatar } from 'components/layout/Avatar';
import { CATEGORICAL_COLORS } from './categoricalColors';
import { StackedBarSeries } from './StackedBarChart';

/*
 * EmployeeBarList.tsx
 * Card listing employees stacked vertically, each row showing the
 * employees profile picture on the left and, on the right, their
 * name above a horizontal bar sized relative to the highest total
 * among all employees. The bar can carry multiple stacked segments
 * when more than one series is given, sharing the same series/color
 * config shape as StackedBarChart so both can read the same data.
 */

export interface EmployeeBarDataPoint {
    group: string;
    label?: string;
    user_id?: number;
    image?: string | null;
    [seriesKey: string]: string | number | null | undefined;
}

interface EmployeeBarListProps {
    title: string;
    tooltipText?: string;
    titleClassName?: string;
    data: EmployeeBarDataPoint[];
    series: StackedBarSeries[];
    loading?: boolean;
    valueFormatter?: (value: number) => string;
    onEmployeeClick?: (point: EmployeeBarDataPoint) => void;
}

const EmployeeBarList: React.FC<EmployeeBarListProps> = ({
    title, tooltipText, titleClassName, data, series, loading, valueFormatter, onEmployeeClick,
}) => {
    const { t } = useTranslation();
    const formatValue = valueFormatter || ((value: number) => value.toString());

    // Sum the series values into a single total per employee
    const totalOf = (point: EmployeeBarDataPoint) => series.reduce((sum, item) => sum + (Number(point[item.key]) || 0), 0);

    // Only show employees with at least some data, ranked highest total first
    const rows = data
        .filter(point => totalOf(point) > 0)
        .sort((a, b) => totalOf(b) - totalOf(a));

    const maxTotal = rows.reduce((max, point) => Math.max(max, totalOf(point)), 0);

    return (
        <div className='dashboard-card employee-bar-list'>
            <h6 className={titleClassName}>
                {title}
                {tooltipText && (
                    <span className='tooltip-icon'>
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className='tooltip tooltip-right'>{tooltipText}</span>
                    </span>
                )}
            </h6>
            {loading ? (
                <div className='metric-chart-loading'>
                    <div className='loader' />
                </div>
            ) : rows.length === 0 ? (
                <div className='metric-chart-empty'>{t('general.no_data_available')}</div>
            ) : (
                <>
                    <div className='employee-bar-rows'>
                        {rows.map(point => {
                            const total = totalOf(point);
                            const name = point.label ?? point.group;

                            return (
                                <div
                                    key={point.user_id ?? point.group}
                                    className={`employee-bar-row${onEmployeeClick ? ' no-drag clickable' : ''}`}
                                    onClick={onEmployeeClick ? () => onEmployeeClick(point) : undefined}
                                >
                                    <div className='employee-bar-avatar'>
                                        {point.image ? (
                                            <div style={{ backgroundImage: `url(${point.image})` }} className='resource-image' />
                                        ) : (
                                            <Avatar name={name} size='2rem' />
                                        )}
                                    </div>
                                    <div className='employee-bar-content'>
                                        <div className='employee-bar-name'>{name}</div>
                                        <div className='employee-bar-track'>
                                            {series.map((item, index) => {
                                                const value = Number(point[item.key]) || 0;
                                                if (value <= 0) return null;

                                                return (
                                                    <div
                                                        key={item.key}
                                                        className='employee-bar-segment tooltip-icon'
                                                        style={{
                                                            width: `${maxTotal > 0 ? (value / maxTotal) * 100 : 0}%`,
                                                            backgroundColor: item.color || CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length],
                                                        }}
                                                    >
                                                        <span className='tooltip'>{item.label} ({formatValue(value)})</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className='employee-bar-total'>{formatValue(total)}</div>
                                </div>
                            );
                        })}
                    </div>
                    {series.length > 1 && (
                        <div className='donut-chart-legend'>
                            {series.map((item, index) => (
                                <div key={item.key} className='donut-chart-legend-item'>
                                    <span className='dot' style={{ backgroundColor: item.color || CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length] }} />
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EmployeeBarList;
